"use client";

import { emailSchema, passwordLoginSchema } from "@repo/api/lib/validation";
import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { safeInternalPath } from "@repo/utils";
import { getAuthErrorMessage } from "@saas/auth/client";
import { authQueryKeys } from "@saas/auth/lib/api";
import { OrganizationInvitationAlert } from "@saas/organizations/components/OrganizationInvitationAlert";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useRouter } from "@shared/hooks/router";
import { useForm, useStore } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { Button } from "@ui/components/button";
import { Field, FieldError, FieldLabel } from "@ui/components/field";
import { Input } from "@ui/components/input";
import { PasswordInput } from "@ui/components/password-input";
import { Spinner } from "@ui/components/spinner";
import { Tabs, TabsList, TabsTrigger } from "@ui/components/tabs";
import { FingerprintIcon, MailCheckIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useSession } from "../hooks/use-session";
import {
	AuthDone,
	AuthError,
	authErrorInput,
	visibleFieldError,
} from "./AuthNotice";

type Mode = "password" | "magic-link";

interface LoginSearch {
	invitationId?: string;
	email?: string;
	redirectTo?: string;
}

interface SignInValues {
	email: string;
	password: string;
	mode: Mode;
}

/** Where to go once signed in: the invitation being accepted, else the page asked for. */
function redirectPathFor(search: LoginSearch) {
	if (search.invitationId) {
		return `/organization-invitation/${search.invitationId}`;
	}
	// safeInternalPath: redirectTo comes straight off the query string, so an
	// unchecked value here is an open redirect once it reaches router.replace.
	return (
		safeInternalPath(search.redirectTo) ?? config.auth.redirectAfterSignIn
	);
}

/** Signs in with the form's values. "two-factor" means a code is still needed. */
async function signIn(
	value: SignInValues,
	callbackURL: string,
): Promise<"signed-in" | "two-factor" | "link-sent"> {
	if (value.mode === "password") {
		const { data, error } = await authClient.signIn.email({
			email: value.email,
			password: value.password,
		});
		if (error) {
			throw error;
		}
		return data && "twoFactorRedirect" in data && data.twoFactorRedirect
			? "two-factor"
			: "signed-in";
	}
	const { error } = await authClient.signIn.magicLink({
		email: value.email,
		callbackURL,
	});
	if (error) {
		throw error;
	}
	return "link-sent";
}

export function LoginForm() {
	const router = useRouter();
	const queryClient = useQueryClient();
	const searchParams = useSearch({ strict: false }) as LoginSearch;
	const { user, loaded: sessionLoaded } = useSession();

	const [rootError, setRootError] = useState<string | null>(null);
	const [sentTo, setSentTo] = useState<string | null>(null);
	const invitationId = searchParams.invitationId;
	const redirectPath = redirectPathFor(searchParams);

	const form = useForm({
		defaultValues: {
			email: searchParams.email ?? "",
			password: "",
			mode: (config.auth.enablePasswordLogin
				? "password"
				: "magic-link") as Mode,
		},
		onSubmit: async ({ value }) => {
			setRootError(null);
			try {
				const outcome = await signIn(value, redirectPath);
				if (outcome === "two-factor") {
					router.navigate({ to: "/verify", search: searchParams });
					return;
				}
				if (outcome === "link-sent") {
					setSentTo(value.email);
					return;
				}
				await queryClient.invalidateQueries({
					queryKey: authQueryKeys.session(),
				});
				router.replace(redirectPath);
			} catch (error) {
				setRootError(getAuthErrorMessage(authErrorInput(error)));
			}
		},
	});

	useEffect(() => {
		if (sessionLoaded && user) {
			router.replace(redirectPath);
		}
	}, [user, sessionLoaded, router, redirectPath]);

	const mode = useStore(form.store, (state) => state.values.mode);
	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
	const attempted = useStore(
		form.store,
		(state) => state.submissionAttempts > 0,
	);

	if (sentTo) {
		return <LinkSent email={sentTo} onBack={() => setSentTo(null)} />;
	}

	return (
		<div className="flex flex-col">
			<AuthHeading
				title="Sign in"
				description={
					invitationId
						? "Use the email your invitation was sent to."
						: undefined
				}
			/>

			<div className="flex flex-col gap-5">
				{invitationId ? <OrganizationInvitationAlert /> : null}
				{import.meta.env.DEV ? (
					<DevQuickLogin
						onPick={(email) => {
							form.setFieldValue("email", email);
							form.setFieldValue("password", "TestPassword123!");
							form.setFieldValue("mode", "password");
						}}
					/>
				) : null}

				<ModeTabs
					mode={mode}
					onChange={(next) => {
						setRootError(null);
						form.setFieldValue("mode", next);
					}}
				/>

				<AuthError message={rootError} />

				<form
					className="flex flex-col gap-5"
					noValidate
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field
						name="email"
						validators={{ onBlur: emailSchema }}
					>
						{(field) => {
							const error = visibleFieldError(field, attempted);
							const invalid = !!error;
							return (
								<Field data-invalid={invalid || undefined}>
									<FieldLabel htmlFor="email">
										Work email
									</FieldLabel>
									<Input
										id="email"
										type="email"
										placeholder="you@retailer.com"
										value={field.state.value}
										onChange={(event) =>
											field.handleChange(
												event.target.value,
											)
										}
										onBlur={field.handleBlur}
										autoComplete="username webauthn"
										autoFocus={!searchParams.email}
										aria-invalid={invalid || undefined}
									/>
									{invalid ? (
										<FieldError errors={error} />
									) : null}
								</Field>
							);
						}}
					</form.Field>

					{config.auth.enablePasswordLogin && mode === "password" ? (
						<form.Field
							name="password"
							validators={{ onBlur: passwordLoginSchema }}
						>
							{(field) => {
								const error = visibleFieldError(
									field,
									attempted,
								);
								const invalid = !!error;
								return (
									<Field data-invalid={invalid || undefined}>
										<div className="flex items-center justify-between gap-4">
											<FieldLabel htmlFor="password">
												Password
											</FieldLabel>
											<Link
												to="/forgot-password"
												className="text-muted-foreground text-xs underline-offset-4 hover:text-foreground hover:underline"
											>
												Forgot password?
											</Link>
										</div>
										<PasswordInput
											id="password"
											value={field.state.value}
											onChange={field.handleChange}
											onBlur={field.handleBlur}
											autoComplete="current-password"
											autoFocus={!!searchParams.email}
											aria-invalid={invalid || undefined}
										/>
										{invalid ? (
											<FieldError errors={error} />
										) : null}
									</Field>
								);
							}}
						</form.Field>
					) : null}

					<Button type="submit" size="lg" disabled={isSubmitting}>
						{isSubmitting ? (
							<Spinner data-icon="inline-start" />
						) : null}
						{mode === "magic-link"
							? "Email me a sign-in link"
							: "Sign in"}
					</Button>
				</form>

				{config.auth.enablePasskeys ? (
					<PasskeySignIn
						redirectPath={redirectPath}
						onError={setRootError}
					/>
				) : null}

				<SignUpPrompt search={searchParams} />
			</div>
		</div>
	);
}

function DevQuickLogin({ onPick }: { onPick: (email: string) => void }) {
	return (
		<div className="flex items-center gap-2 rounded-2xl border border-border border-dashed p-2 pl-3 text-xs">
			<span className="text-muted-foreground">Dev</span>
			<div className="ml-auto flex gap-1">
				<Button
					size="xs"
					variant="ghost"
					onClick={() => onPick("test@example.com")}
				>
					Test User
				</Button>
				<Button
					size="xs"
					variant="ghost"
					onClick={() => onPick("test2@example.com")}
				>
					Test User 2
				</Button>
			</div>
		</div>
	);
}

function LinkSent({ email, onBack }: { email: string; onBack: () => void }) {
	return (
		<AuthDone
			icon={<MailCheckIcon />}
			title="Check your inbox"
			actions={
				<Button variant="outline" onClick={onBack}>
					Use a different email
				</Button>
			}
		>
			We sent a sign-in link to{" "}
			<span className="font-medium text-foreground">{email}</span>. Open
			it on this device to continue. It can take a minute to arrive.
		</AuthDone>
	);
}

/** Passkey sign-in; clears the form's error when it starts and reports its own. */
function PasskeySignIn({
	redirectPath,
	onError,
}: {
	redirectPath: string;
	onError: (message: string | null) => void;
}) {
	const router = useRouter();
	const [pending, setPending] = useState(false);

	const signInWithPasskey = async () => {
		onError(null);
		setPending(true);
		try {
			const result = await authClient.signIn.passkey();
			if (result?.error) {
				throw result.error;
			}
			router.replace(redirectPath);
		} catch (error) {
			onError(getAuthErrorMessage(authErrorInput(error)));
		} finally {
			setPending(false);
		}
	};

	return (
		<>
			<div className="flex items-center gap-3 text-muted-foreground text-xs">
				<span className="h-px flex-1 bg-border" />
				or
				<span className="h-px flex-1 bg-border" />
			</div>
			<Button
				variant="outline"
				size="lg"
				onClick={signInWithPasskey}
				disabled={pending}
			>
				{pending ? (
					<Spinner data-icon="inline-start" />
				) : (
					<FingerprintIcon data-icon="inline-start" />
				)}
				Sign in with a passkey
			</Button>
		</>
	);
}

function SignUpPrompt({ search }: { search: LoginSearch }) {
	if (!config.auth.enableSignup) {
		return (
			<p className="text-pretty text-muted-foreground text-xs">
				No account? Ask your admin for an invitation.
			</p>
		);
	}
	return (
		<p className="text-center text-muted-foreground text-sm">
			New here?{" "}
			<Link
				to="/signup"
				search={search}
				className="font-medium text-foreground underline-offset-4 hover:underline"
			>
				Create an account
			</Link>
		</p>
	);
}

/** Password or emailed link, shown only when both ways to sign in are on. */
function ModeTabs({
	mode,
	onChange,
}: {
	mode: Mode;
	onChange: (mode: Mode) => void;
}) {
	if (!(config.auth.enableMagicLink && config.auth.enablePasswordLogin)) {
		return null;
	}
	return (
		<Tabs value={mode} onValueChange={(value) => onChange(value as Mode)}>
			<TabsList className="w-full">
				<TabsTrigger value="password">Password</TabsTrigger>
				<TabsTrigger value="magic-link">Email me a link</TabsTrigger>
			</TabsList>
		</Tabs>
	);
}
